import React, { forwardRef, useState } from "react";
import axios from "axios";
import { PRODUCTS } from "../data/copperx";
import { Check, Loader2, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONTRACTOR_TYPES = [
  "MEP Contractor",
  "HVAC Contractor",
  "Mechanical Engineer",
  "Consultant",
  "Procurement Manager",
  "Facility Management",
  "Developer",
  "Refrigeration Contractor",
  "Industrial Project / OEM",
  "Other",
];

const INITIAL_FORM = {
  full_name: "",
  company: "",
  email: "",
  phone: "",
  country: "United Arab Emirates",
  contractor_type: "",
  products: [],
  quantity: "",
  message: "",
};

const REQUIRED_FIELDS = ["full_name", "company", "email", "phone"];

function validateForm(form) {
  for (const field of REQUIRED_FIELDS) {
    if (!form[field]) return "Please fill in name, company, email and phone.";
  }
  return "";
}

const QuoteForm = forwardRef(function QuoteForm(_, ref) {
  const [form, setForm] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const toggleProduct = (name) => {
    setForm((f) =>
      f.products.includes(name)
        ? { ...f, products: f.products.filter((p) => p !== name) }
        : { ...f, products: [...f.products, name] }
    );
  };

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const validationError = validateForm(form);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      await axios.post(`${API}/quotes`, form);
      setSuccess(true);
      setForm(INITIAL_FORM);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not submit. Please try again or call us.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      ref={ref}
      id="quote"
      data-testid="quote-section"
      className="bg-[#F8F9FA] py-24 md:py-32 border-t border-[#E2E8F0]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3 mb-5">
            <span className="section-rule" />
            <span className="text-[#B85C38] text-xs tracking-[0.3em] uppercase font-semibold">
              Request a Quote
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight text-[#0F172A] leading-[1.05] mb-6">
            Let's spec your <span className="text-[#B85C38]">project together.</span>
          </h2>
          <p className="text-[#475569] text-lg leading-relaxed mb-10">
            Share your application, sizes, and quantities. Our technical team replies
            within one working day with availability, lead-times, and a delivered price for your site.
          </p>

          <div className="border-l-4 border-[#B85C38] bg-white p-6 industrial-shadow">
            <div className="font-mono text-xs text-[#B85C38] tracking-widest uppercase mb-3">
              Direct line
            </div>
            <a
              href="tel:+971507468523"
              className="text-2xl font-medium text-[#0F172A] block mb-2 hover:text-[#B85C38] transition-colors"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              +971 50 746 8523
            </a>
            <a
              href="mailto:ssrcopperxtrading@gmail.com"
              className="text-[#475569] hover:text-[#B85C38] transition-colors text-sm font-mono break-all"
            >
              ssrcopperxtrading@gmail.com
            </a>
          </div>
        </div>

        <div className="lg:col-span-7">
          {success ? (
            <div
              data-testid="quote-success-card"
              className="bg-white border-l-4 border-[#16a34a] industrial-shadow p-10"
            >
              <div className="w-14 h-14 bg-[#dcfce7] text-[#16a34a] flex items-center justify-center mb-6">
                <Check size={28} />
              </div>
              <h3 className="text-3xl font-medium text-[#0F172A] mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>
                Quote request received.
              </h3>
              <p className="text-[#475569] text-base leading-relaxed mb-6">
                Thank you — our technical team will reach out within one working day.
                In the meantime, feel free to call us directly on +971 50 746 8523.
              </p>
              <button
                onClick={() => setSuccess(false)}
                data-testid="quote-submit-another-button"
                className="text-[#B85C38] font-medium tracking-wide hover:text-[#9B5633]"
              >
                Submit another request →
              </button>
            </div>
          ) : (
            <form
              onSubmit={submit}
              data-testid="quote-form"
              className="bg-white p-8 md:p-10 industrial-shadow border border-[#E2E8F0] space-y-6"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Full Name *" name="full_name" value={form.full_name} onChange={onChange} testid="quote-input-name" />
                <Field label="Company *" name="company" value={form.company} onChange={onChange} testid="quote-input-company" />
                <Field label="Email *" name="email" type="email" value={form.email} onChange={onChange} testid="quote-input-email" />
                <Field label="Phone *" name="phone" value={form.phone} onChange={onChange} testid="quote-input-phone" />
                <Field label="Country" name="country" value={form.country} onChange={onChange} testid="quote-input-country" />
                <SelectField
                  label="I am a…"
                  name="contractor_type"
                  value={form.contractor_type}
                  onChange={onChange}
                  options={CONTRACTOR_TYPES}
                  testid="quote-select-contractor-type"
                />
              </div>

              <div>
                <label className="block text-xs tracking-[0.2em] uppercase font-semibold text-[#0F172A] mb-3">
                  Products of interest
                </label>
                <div className="flex flex-wrap gap-2">
                  {PRODUCTS.map((p) => {
                    const selected = form.products.includes(p.name);
                    return (
                      <button
                        type="button"
                        key={p.id}
                        onClick={() => toggleProduct(p.name)}
                        data-testid={`quote-product-chip-${p.id}`}
                        className={`px-4 py-2 text-sm font-mono border transition-colors ${
                          selected
                            ? "bg-[#B85C38] text-white border-[#B85C38]"
                            : "bg-white text-[#0F172A] border-[#E2E8F0] hover:border-[#B85C38]"
                        }`}
                      >
                        {p.name}
                      </button>
                    );
                  })}
                </div>
              </div>

              <Field label="Estimated quantity / sizes" name="quantity" placeholder="e.g. 500 m of 9.52mm soft coil + 200 m of 22mm straight tube" value={form.quantity} onChange={onChange} testid="quote-input-quantity" />

              <div>
                <label className="block text-xs tracking-[0.2em] uppercase font-semibold text-[#0F172A] mb-2">
                  Project notes
                </label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  rows={4}
                  data-testid="quote-textarea-message"
                  placeholder="Application, site location, delivery deadline, any specific ASTM grade…"
                  className="w-full bg-[#F8F9FA] border border-[#E2E8F0] focus:border-[#B85C38] focus:ring-1 focus:ring-[#B85C38] outline-none px-4 py-3 text-sm text-[#0F172A]"
                />
              </div>

              {error && (
                <div data-testid="quote-error" className="text-sm text-[#b91c1c] bg-[#fef2f2] border border-[#fecaca] px-4 py-3">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                data-testid="quote-submit-button"
                className="w-full md:w-auto bg-[#18181B] hover:bg-[#27272A] text-white px-8 py-4 font-medium tracking-wide flex items-center justify-center gap-3 transition-colors disabled:opacity-60"
              >
                {submitting ? (
                  <>
                    <Loader2 className="animate-spin" size={18} /> Submitting…
                  </>
                ) : (
                  <>
                    Submit Quote Request <Send size={16} />
                  </>
                )}
              </button>
              <p className="text-xs text-[#94a3b8] font-mono">
                By submitting you agree to be contacted by SSR CopperX about your inquiry.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
});

function Field({ label, name, value, onChange, type = "text", placeholder, testid }) {
  return (
    <div>
      <label className="block text-xs tracking-[0.2em] uppercase font-semibold text-[#0F172A] mb-2">
        {label}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full bg-[#F8F9FA] border border-[#E2E8F0] focus:border-[#B85C38] focus:ring-1 focus:ring-[#B85C38] outline-none px-4 py-3 text-sm text-[#0F172A]"
      />
    </div>
  );
}

function SelectField({ label, name, value, onChange, options, testid }) {
  return (
    <div>
      <label className="block text-xs tracking-[0.2em] uppercase font-semibold text-[#0F172A] mb-2">
        {label}
      </label>
      <select
        name={name}
        value={value}
        onChange={onChange}
        data-testid={testid}
        className="w-full bg-[#F8F9FA] border border-[#E2E8F0] focus:border-[#B85C38] focus:ring-1 focus:ring-[#B85C38] outline-none px-4 py-3 text-sm text-[#0F172A]"
      >
        <option value="">Select…</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}

export default QuoteForm;
